import { Link, matchPath, useLocation } from "react-router";
import type { ReactNode } from "react";

import { CATALOG, FACTORY, MESSAGING } from "@/lib/sections";

interface NavItem {
  /** Where the entry lands. */
  to: string;
  label: string;
  icon: ReactNode;
  /**
   * The path prefixes that keep the entry lit: the whole section, not just its landing page (on
   * "/catalog/components" the entry "Catalog" lands on "/catalog/gateways" and must still be
   * current). Datacenter is exact: every path starts with "/".
   */
  within: string[];
  exact?: boolean;
}

// Hand-drawn 18 px icons, kept as they were: same strokes, same weight (currentColor, so they take
// the entry's text colour, white on the current one).
const iconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const DATACENTER: NavItem = {
  to: "/",
  exact: true,
  within: ["/"],
  label: "Datacenter",
  icon: (
    <svg {...iconProps} aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
};

// Two groups, because the console does two jobs and they should not be read as one list: what an
// operator runs FOR the customers (OPERATE) and the tooling that builds and ships the platform
// (PLATFORM). The order inside each is the order of use.
const GROUPS: { id: string; label: string; items: NavItem[] }[] = [
  {
    id: "operate",
    label: "Operate",
    items: [
      {
        to: "/tenants",
        // The onboarding wizard is where a tenant is created: it belongs to this entry.
        within: ["/tenants", "/onboarding"],
        label: "Tenants",
        icon: (
          <svg {...iconProps} aria-hidden="true">
            <path d="M3 21V7l7-4 7 4v14" />
            <path d="M9 21v-6h2v6" />
            <path d="M17 21h4V11l-4-2" />
          </svg>
        ),
      },
      {
        to: CATALOG.tabs[0].to,
        within: ["/catalog"],
        label: CATALOG.label,
        icon: (
          <svg {...iconProps} aria-hidden="true">
            <circle cx="12" cy="5" r="2" />
            <circle cx="5" cy="19" r="2" />
            <circle cx="19" cy="19" r="2" />
            <path d="M12 7v4M12 11l-5 6M12 11l5 6" />
          </svg>
        ),
      },
      {
        // The journal of what was sent, the templates and the campaigns: the entry lands on the
        // first tab and stays lit on the others.
        to: MESSAGING.tabs[0].to,
        within: ["/messaging"],
        label: MESSAGING.label,
        icon: (
          <svg {...iconProps} aria-hidden="true">
            <path d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        ),
      },
      {
        // "recordings" and not what indexes them today: the address has already survived a change
        // of cache key (isrc → acrid), so it names what one looks at, not what keys it.
        to: "/metadata/recordings",
        within: ["/metadata"],
        label: "Metadata",
        icon: (
          <svg {...iconProps} aria-hidden="true">
            <path d="M9 18V5l12-2v13" />
            <path d="M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 19a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
          </svg>
        ),
      },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    items: [
      {
        to: "/integrations",
        within: ["/integrations"],
        label: "Integrations",
        icon: (
          <svg {...iconProps} aria-hidden="true">
            <path d="M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0z" />
            <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
          </svg>
        ),
      },
      {
        to: "/audience-classification",
        within: ["/audience-classification"],
        label: "Audience classification",
        icon: (
          <svg {...iconProps} aria-hidden="true">
            <rect x="5" y="8" width="14" height="11" rx="2" />
            <path d="M12 8V4M9 13h.01M15 13h.01M9.5 16.5h5" />
          </svg>
        ),
      },
      {
        to: "/measurement-presets",
        within: ["/measurement-presets"],
        label: "Measurement presets",
        icon: (
          <svg {...iconProps} aria-hidden="true">
            <path d="M4 20h16" />
            <path d="M7 16V10M12 16V6M17 16v-4" />
          </svg>
        ),
      },
      {
        to: "/builds",
        within: ["/builds"],
        label: "Builds",
        icon: (
          <svg {...iconProps} aria-hidden="true">
            <path d="M21 16V8l-9-5-9 5v8l9 5 9-5z" />
            <path d="M3.3 7.3 12 12l8.7-4.7M12 12v9" />
          </svg>
        ),
      },
      {
        // The internal tooling: what builds and deploys, as opposed to what is run for the
        // customers. The entry lands on the first tab and stays lit on the other.
        to: FACTORY.tabs[0].to,
        within: ["/factory"],
        label: FACTORY.label,
        icon: (
          <svg {...iconProps} aria-hidden="true">
            <path d="M3 20h18" />
            <path d="M5 20V9l5 3V9l5 3V9l4 2v9" />
            <path d="M8 20v-3M16 20v-3" />
          </svg>
        ),
      },
      {
        // The backlog of both repositories, seen from the organisation's GitHub Project. It comes
        // last: it is planning, not operating, and nobody reads it in an emergency.
        to: "/backlog",
        within: ["/backlog"],
        label: "Backlog",
        icon: (
          <svg {...iconProps} aria-hidden="true">
            <path d="M4 6h16M4 12h16M4 18h9" />
            <path d="m17 17.5 1.5 1.5 3-3" />
          </svg>
        ),
      },
    ],
  },
];

// One class list per state, never two that both set a colour: a current entry is a solid fill
// with white text, the others sit on the chrome and darken a step under the pointer.
const ITEM = "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition";
const ITEM_CURRENT = "bg-brand-600 text-on-fill";
const ITEM_OTHER = "text-ink hover:bg-sunken-deep";

// The label of a group (and of the infrastructure tree under the entries): 12 px, capitals, muted.
const GROUP_LABEL = "mb-1 mt-3.5 px-3 text-xs font-semibold uppercase tracking-wider text-ink-muted";

function Entries({ items, collapsed }: { items: NavItem[]; collapsed: boolean }) {
  const { pathname } = useLocation();
  return (
    <ul className="flex flex-col gap-0.5">
      {items.map((item) => {
        const current = item.within.some((prefix) => matchPath({ path: prefix, end: item.exact ?? false }, pathname));
        return (
          <li key={item.to}>
            <Link
              to={item.to}
              aria-current={current ? "page" : undefined}
              // Collapsed, the entry is an icon (aria-hidden): its name is the label, and the tooltip
              // ``title`` is only a description for a screen reader, not a reliable name.
              aria-label={collapsed ? item.label : undefined}
              title={collapsed ? item.label : undefined}
              className={`${ITEM} ${current ? ITEM_CURRENT : ITEM_OTHER}${collapsed ? " justify-center" : ""}`}
            >
              <span className="shrink-0">{item.icon}</span>
              {!collapsed && <span className="truncate">{item.label}</span>}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The entries of the console: the home page on its own, then the two groups. Collapsed, the
 * sidebar is a column of icons: a group's name is then only for a screen reader, and a rule
 * separates one group from the next.
 */
export function SidebarNav({ collapsed }: { collapsed: boolean }) {
  return (
    <>
      <Entries items={[DATACENTER]} collapsed={collapsed} />
      {GROUPS.map((group) => (
        <div key={group.id} className={collapsed ? "mt-2 border-t border-line pt-2" : undefined}>
          <p id={`nav-group-${group.id}`} className={collapsed ? "sr-only" : GROUP_LABEL}>
            {group.label}
          </p>
          <div role="group" aria-labelledby={`nav-group-${group.id}`}>
            <Entries items={group.items} collapsed={collapsed} />
          </div>
        </div>
      ))}
    </>
  );
}
